import { useEffect, useState } from 'react';

import dynamic from 'next/dynamic';

import { HeartAnimation } from 'src/app/components/Elements/HeartAnimation';
import Spinner from 'src/app/components/Elements/Spinner';
import ProgressForm from 'src/app/components/Form/ProgressForm';
import { INTERSTITIAL_TIMING } from 'src/consts';

const CenteredSpinner = () => <Spinner centered />;
const Welcome = dynamic(() => import('./Steps/WelcomeStep'), { loading: CenteredSpinner, ssr: false });
const Confirmation = dynamic(() => import('./Steps/ConfirmationStep'), { loading: CenteredSpinner, ssr: false });
const Credentials = dynamic(() => import('./Steps/CredentialsStep/container'), {
  loading: CenteredSpinner,
  ssr: false,
});

const loginSetSteps = [
  {
    label: 'welcome',
    component: Welcome,
  },
  {
    label: 'confirmation',
    component: Confirmation,
  },
];

const loginUnsetSteps = [
  {
    label: 'welcome',
    component: Welcome,
  },
  {
    label: 'credentials',
    component: Credentials,
  },
  {
    label: 'confirmation',
    component: Confirmation,
  },
];

const getSteps = ({ isLoginSet }: { isLoginSet: boolean }) => (isLoginSet ? loginSetSteps : loginUnsetSteps);

const StepWrapper = ({ showHorizontalProgressBar, setShowHorizontalProgressBar, children }) => {
  useEffect(() => {
    setShowHorizontalProgressBar(showHorizontalProgressBar);
  }, [setShowHorizontalProgressBar, showHorizontalProgressBar]);

  return children;
};

export const PartnerOnboardingFlow = ({
  currentUser,
  currentUserEmail,
  dispatchFetchPatient,
  dispatchHideNavLinks,
  dispatchLoadHomescreenMedications,
  isFetchingMedications,
  isFetchingPatient,
  isLoginSet,
  isOnboardingFlowEnabled,
  onResetNavBar,
  onToggleHideHeader,
  patient,
  pendingTransfers,
  promo,
  redirectHome,
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [showInterstitial, setShowInterstitial] = useState(false);
  const [steps] = useState(getSteps({ isLoginSet }));

  const currentStep = steps[currentStepIndex];
  const StepComponent = currentStep?.component;

  const handlePrev = () => {
    setCurrentStepIndex(Math.max(currentStepIndex - 1, 0));
  };

  const handleNextClick = () => {
    const requestedStep = currentStepIndex + 1;
    // requestedStep is 0-indexed, but length is not
    if (requestedStep > steps.length - 1) {
      onToggleHideHeader(true);
      setShowInterstitial(true);
      window.setTimeout(() => {
        onResetNavBar();
        redirectHome();
      }, INTERSTITIAL_TIMING);
    } else {
      onToggleHideHeader(false);
      setCurrentStepIndex(requestedStep);
    }
  };

  // Redirect if invalid promo or feature disabled
  useEffect(() => {
    if (promo !== 'healthfirst' || !isOnboardingFlowEnabled) {
      redirectHome();
    }
  }, [isOnboardingFlowEnabled, promo, redirectHome]);

  // Fetch Patient Data on Mount
  useEffect(() => {
    Promise.all([dispatchFetchPatient(), dispatchLoadHomescreenMedications()]);
  }, [dispatchFetchPatient, dispatchLoadHomescreenMedications]);

  useEffect(() => {
    dispatchHideNavLinks();

    if (currentStepIndex === 0) {
      onToggleHideHeader(true);
    }
  }, [currentStepIndex, dispatchHideNavLinks, onToggleHideHeader]);

  if (showInterstitial) return <HeartAnimation />;

  const isCredentialsStep = currentStep?.label === 'credentials';

  return (
    <ProgressForm
      // When we're on the credentials step, we want to render "1 of 1"
      steps={isCredentialsStep ? ['credentials'] : steps.map(step => step.label)}
      currentStepIndex={isCredentialsStep ? 0 : currentStepIndex}
      showPreviousButton={isCredentialsStep}
      isHorizontalProgressBarVisible={false}
      isVerticalProgressBarVisible={false}
      onPrev={handlePrev}
      hasStepHeader={currentStepIndex !== 0 && currentStepIndex !== steps.length - 1}
      showCopyrightFooter={false}
      mainRowPaddingBottom="none"
      customHandlePrevText="Back"
    >
      <StepWrapper showHorizontalProgressBar={isCredentialsStep}>
        <StepComponent
          currentUserEmail={currentUserEmail}
          isFetchingCurrentUser={!currentUser}
          isFetchingMedications={isFetchingMedications}
          isFetchingPatient={isFetchingPatient}
          onNextClick={handleNextClick}
          patient={patient}
          pendingTransfers={pendingTransfers}
        />
      </StepWrapper>
    </ProgressForm>
  );
};
